import { useEffect }          from 'react';
import { useStore }           from 'react-redux';

import { RootState }          from '../../../store/generalReducer';
import { ActionType }         from '../../../store/adminAuthenticationManagment/types';
import { getAdminResources }  from '../../../backendServices/adminManagment/api';

/**
 * Tient à jour, en quasi temps réel, les compteurs de l'en-tête (Devices, Chaînes, Stockage).
 *
 * Ces trois valeurs vivent dans `authentication.admin`, rafraîchi seulement à la connexion et
 * après certaines actions (achat, bascule de chaîne, suppression de média) : un appareil qui se
 * connecte, une chaîne activée ailleurs ou un enregistrement qui grossit n'y paraissaient pas.
 *
 * Le relevé passe par le récapitulatif de ressources du créateur (≈ 20 ms, 500 octets) plutôt que
 * par `refreshAdmin` : celui-ci relit tout le compte et interroge un service de géolocalisation.
 * Et il ne touche au magasin QUE si une valeur a changé — une écriture inutile ferait redessiner
 * tout ce qui lit `authentication`, régie comprise.
 *
 * Le relevé s'arrête quand l'onglet est caché, et repart aussitôt qu'il redevient visible.
 */
const POLL_INTERVAL_MS = 15000;

export const useLiveAdminUsage = ( adminId: number | null ): void => {

  const store = useStore<RootState>();

  useEffect ( () => {

    if ( adminId === null ) { return; }

    let cancelled = false;
    let inFlight  = false;

    const refresh = async () => {

      if ( cancelled || inFlight || document.hidden ) { return; }

      inFlight = true;

      try
       {
        const result = await getAdminResources ( adminId );

        if ( cancelled || result.code !== 1 ) { return; }

        const q       = result.payload.quotas;
        const current = store.getState().authenticationReducer.authentication.admin;

        // Le compte a changé (déconnexion, autre session) pendant le relevé : on n'écrit rien.
        if ( !current || current.id !== adminId ) { return; }

        const patch = {
          nbDevicesUsed  : q.devices.used  ,
          nbDevicesMax   : q.devices.max   ,
          nbChannelsUsed : q.channels.used ,
          nbChannelsMax  : q.channels.max  ,
          quotaUsed      : q.disk.used     ,
          quotaDisk      : q.disk.max      ,
        };

        const changed = ( Object.keys ( patch ) as Array<keyof typeof patch> )
                          .some ( key => typeof patch[key] === 'number' && current[key] !== patch[key] );

        if ( changed )
         {
          store.dispatch ( { type: ActionType.SET_ADMIN , admin: { ...current , ...patch } } as any );
         }
       }
      catch
       {
        // Un relevé manqué n'a aucune conséquence : le suivant rattrape.
       }
      finally
       {
        inFlight = false;
       }
    };

    const timer = window.setInterval ( refresh , POLL_INTERVAL_MS );

    document.addEventListener ( 'visibilitychange' , refresh );
    window.addEventListener   ( 'focus'            , refresh );

    refresh ();

    return () => {
      cancelled = true;
      window.clearInterval ( timer );
      document.removeEventListener ( 'visibilitychange' , refresh );
      window.removeEventListener   ( 'focus'            , refresh );
    };

  }, [ adminId , store ] );
};
